"use client";
import { Api } from "@/Apis";
import {
  LandingItemGetDetailExtraOutputApiModel,
  LandingItemGetDetailOutputApiModel,
  LandingSiteTemplateGetDataOutputApiModel,
} from "@/Apis/ClinicApi";
import {
  CenterBranchTag,
  CenterProductTag,
} from "@/Areas/Center/Components/Tags";
import {
  CenterInvoiceItemCount,
  LandingBlogsCard,
  LandingCenterDepartmentsCard,
  LandingCenterGalleryCategoriesCard,
  LandingCenterItemsCard,
  LandingCenterPersonnelsCard,
} from "@/Areas/Landing/Components";
import { LandingCenterProductsSelect } from "@/Areas/Landing/Components/Selects";
import { useLandingCheckout } from "@/Areas/Landing/Hooks";
import {
  ActionButton,
  ButtonColor,
  FavoriteToggle,
  Format,
  Html,
  If,
} from "@/Components";
import { useRouter } from "@/Components/AppProgressBar";
import { Colors, Constants } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useAccount, useApp, useResponsive, useT } from "@/Hooks";
import { Faq } from "@/Templates/Doccure/Sections";
import { defaultFaqSetting } from "@/Templates/Doccure/Sections/Faq";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Image,
  Row,
  Skeleton,
  Space,
  Typography,
} from "antd";
import _ from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { FaShareAlt } from "react-icons/fa";
import { FiGrid } from "react-icons/fi";
import { LandingCommentListPage } from "../../LandingComment";

export interface LandingItemDetailPageClientProps {
  data: LandingItemGetDetailOutputApiModel;
  layoutData: LandingSiteTemplateGetDataOutputApiModel;
}

const LandingItemDetailPageClient: FC<LandingItemDetailPageClientProps> = ({
  data,
  layoutData,
}) => {
  const t = useT();
  const { message, openShare } = useApp();
  const { profile, isSignIn, centerBranchSetting } = useAccount();
  const { isMobile } = useResponsive();
  const [productId, setProductId] = useState<number | undefined>(undefined);
  const router = useRouter();
  // const [result, setResult] = useState<LandingItemGetDetailOutputApiModel>()
  const { addItemToCart } = useLandingCheckout();
  const [extraData, setExtraData] =
    useState<LandingItemGetDetailExtraOutputApiModel | null>(null);
  const [extraLoading, setExtraLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const loadExtraData = async () => {
      try {
        setExtraLoading(true);
        const r = await Api.LandingItem.getDetailExtra(
          { Id: data.Id },
          controller.signal,
        );
        if (r.Success && r.Result) setExtraData(r.Result);
      } catch {
        // ignore abort and transient request failures
      } finally {
        setExtraLoading(false);
      }
    };

    void loadExtraData();
    return () => controller.abort();
  }, [data.Id, data.CenterDepartment.TypeKey]);

  const products = useMemo(
    () => extraData?.Products ?? [],
    [extraData?.Products],
  );
  const centerInvoiceItems = useMemo(
    () => extraData?.CenterInvoiceItems ?? [],
    [extraData?.CenterInvoiceItems],
  );
  const centerFaqs = useMemo(
    () => extraData?.CenterFaqs ?? [],
    [extraData?.CenterFaqs],
  );
  const settingValues = useMemo(
    () => extraData?.SettingValues ?? [],
    [extraData?.SettingValues],
  );
  const relatedDoctors = useMemo(
    () => extraData?.RelatedDoctors ?? [],
    [extraData?.RelatedDoctors],
  );
  const centerGalleryCategories = useMemo(
    () => extraData?.CenterGalleryCategories ?? [],
    [extraData?.CenterGalleryCategories],
  );
  const centerGalleries = useMemo(
    () => extraData?.CenterGalleries ?? [],
    [extraData?.CenterGalleries],
  );
  const relatedItems = useMemo(
    () => extraData?.RelatedItems ?? [],
    [extraData?.RelatedItems],
  );
  const relatedBlogs = useMemo(
    () => extraData?.RelatedBlogs ?? [],
    [extraData?.RelatedBlogs],
  );
  const doctors = useMemo(() => extraData?.Doctors ?? [], [extraData?.Doctors]);
  const departments = useMemo(
    () => extraData?.Departments ?? [],
    [extraData?.Departments],
  );
  const shouldShowCenterDoctors = useMemo(() => {
    if (doctors.length === 0) return false;
    if (relatedDoctors.length === 0) return true;

    const relatedDoctorIds = new Set(
      relatedDoctors.map((item) => item.CenterUserId),
    );
    return doctors.some((item) => !relatedDoctorIds.has(item.CenterUserId));
  }, [doctors, relatedDoctors]);

  useEffect(() => {
    if (!productId && products.length > 0) setProductId(products[0].Id);
  }, [productId, products]);

  const centerInvoiceItem = productId
    ? centerInvoiceItems.find((r) => r.CenterInvoiceProductId == productId)
    : centerInvoiceItems?.[0];
  const singleNoun =
    data.CenterDepartment.TypeKey ==
      CenterOptions.Enum.CenterDepartmentType.Service.key
      ? t("common.field.service", undefined, "Item details")
      : t("common.field.item", undefined, "Item details");
  const pluralNoun =
    data.CenterDepartment.TypeKey ==
      CenterOptions.Enum.CenterDepartmentType.Service.key
      ? t("center.item.plural.services", undefined, "Item details")
      : t("landing.item.detail.products", undefined, "Item details");
  const departmentSingleNoun =
    data.CenterDepartment.TypeKey ==
      CenterOptions.Enum.CenterDepartmentType.Service.key
      ? t("common.field.department", undefined, "Item details")
      : t("common.field.category", undefined, "Item details");
  const departmentPluralNoun =
    data.CenterDepartment.TypeKey ==
      CenterOptions.Enum.CenterDepartmentType.Service.key
      ? t("center.userTag.action.departments", undefined, "Item details")
      : t("center.blog.field.categories", undefined, "Item details");
  const displayName = data.DisplayNameInProfile || data.Name;

  return (
    <Row gutter={[20, 20]}>
      <Col span={24}>
        <Card>
          <Row gutter={[20, 20]}>
            <Col md={10} xs={24}>
              {data.Image && (
                <Card className="text-center">
                  <Image
                    alt={data.Image.AltText ?? displayName}
                    src={data.Image.Url}
                    style={{ minHeight: 400 }}
                  />
                </Card>
              )}
            </Col>
            <Col md={14} xs={24} style={{ padding: "50px 30px" }}>
              <Space orientation="vertical">
                <Typography.Title level={1}>{displayName}</Typography.Title>
                <Space separator={<Divider orientation="vertical" />}>
                  <CenterBranchTag model={data.Center} />
                  <Typography.Title level={4}>
                    <FiGrid color={Colors.Info} /> {data.CenterDepartment.Name}
                  </Typography.Title>
                </Space>
                <If condition={!!data.SalePrice}>
                  <Typography.Title level={3}>
                    <Format
                      type="Number"
                      value={data.SalePrice}
                      currency={data.Center.Currency}
                    />
                  </Typography.Title>
                </If>
                <Typography.Text type="secondary">
                  {t("common.field.visitorCount", undefined, "Visit count")}:{" "}
                  <Format type="Number" value={data.VisitStatistics} />
                </Typography.Text>
                <If condition={(products.length ?? 0) > 0}>
                  <Divider titlePlacement="start">
                    {t(
                      "landing.item.detail.availableInProducts",
                      undefined,
                      "Item details",
                    )}
                  </Divider>
                  <Space>
                    {products.map((product) => (
                      <CenterProductTag key={product.Id} model={product} link />
                    ))}
                  </Space>
                </If>
                <Typography.Paragraph>
                  <Html value={data.ShortDescription} />
                </Typography.Paragraph>
                <Divider />
                <Space
                  align="start"
                  orientation={isMobile ? "vertical" : "horizontal"}
                  separator={
                    <Divider
                      orientation={isMobile ? "horizontal" : "vertical"}
                    />
                  }
                >
                  <If
                    condition={
                      data.CenterDepartment.TypeKey ==
                      CenterOptions.Enum.CenterDepartmentType.Commodity.key
                    }
                  >
                    {extraLoading ? (
                      <Skeleton.Button active size="large" block />
                    ) : !!extraData?.Purchased ? (
                      <ButtonColor
                        block
                        size="large"
                        buttonColor={Colors.Success}
                      >
                        {t(
                          "landing.item.detail.purchased",
                          undefined,
                          "Item details",
                        )}
                      </ButtonColor>
                    ) : extraData?.HasInventory ? (
                      <Space>
                        <If condition={products.length > 1} exists>
                          <LandingCenterProductsSelect
                            CenterItemId={data.Id}
                            value={productId}
                            onChange={setProductId}
                            allowClear={false}
                          />
                        </If>
                        {centerInvoiceItem ? (
                          <Space orientation="vertical">
                            <CenterInvoiceItemCount
                              centerInvoiceItemId={centerInvoiceItem.Id}
                              value={centerInvoiceItem.Count}
                              unit={data.UnitName}
                              onChange={() => router.refresh()}
                              size="large"
                              requiredStatus={
                                centerInvoiceItem.RequiredStatusKey
                              }
                              countStatus={data.CountStatusKey}
                            />
                            <If
                              condition={
                                centerInvoiceItem.StartDate &&
                                centerInvoiceItem.EndDate
                              }
                            >
                              (
                              <Format
                                type="Date"
                                value={centerInvoiceItem.StartDate}
                              />{" "}
                              -{" "}
                              <Format
                                type="Date"
                                value={centerInvoiceItem.EndDate}
                              />
                              )
                            </If>
                          </Space>
                        ) : (
                          <ActionButton
                            type="primary"
                            size="large"
                            onClick={async () => {
                              if (
                                await addItemToCart({
                                  CenterItemId: data.Id,
                                  CenterProductId: productId,
                                }, "Modal", data.Name)
                              )
                                router.refresh();
                            }}
                          >
                            {t(
                              "landing.item.detail.addToCart",
                              undefined,
                              "Item details",
                            )}
                          </ActionButton>
                        )}
                      </Space>
                    ) : (
                      <Typography.Text
                        style={{ fontSize: 25, color: Colors.Secondary }}
                      >
                        {t(
                          "common.status.outOfStock",
                          undefined,
                          "Item details",
                        )}
                      </Typography.Text>
                    )}
                  </If>
                  <Space separator={<Divider orientation="vertical" />}>
                    <FavoriteToggle
                      ids={{
                        type: CenterOptions.Enum.UserFavoriteType.CenterItem
                          .key,
                        centerItemId: data.Id,
                      }}
                      defaultFavorite={extraData?.Favorite}
                      variant="button"
                    />
                    <Button
                      size="large"
                      icon={<FaShareAlt />}
                      onClick={() =>
                        openShare({
                          // link: window.location.href,
                          link: `${Constants.SiteAddress}${data.ShortLink}`,
                          text: t(
                            "landing.item.detail.viewItemInClinic24",
                            { value0: singleNoun, value1: displayName },
                            "Item details",
                          ),
                        })
                      }
                    >
                      {t("common.action.share", undefined, "Item details")}
                    </Button>
                  </Space>
                </Space>
              </Space>
            </Col>
          </Row>
        </Card>
      </Col>
      <If condition={extraLoading || !_.isEmpty(settingValues)}>
        <Col span={24}>
          <Card
            title={t(
              "landing.item.detail.itemFeatures",
              { value0: singleNoun },
              "Item details",
            )}
          >
            {extraLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <Descriptions
                style={{ marginTop: 15 }}
                bordered
                items={settingValues.map((item) => ({
                  key: item.Id,
                  label: item.Setting.Name,
                  span: 3,
                  children: (() => {
                    const content = item.Text ?? item.Value;
                    switch (item.Setting.DataTypeKey) {
                      case CenterOptions.Enum.SettingDataType.TextEditor.key:
                        return <Html value={content} />;
                    }
                    return content;
                  })(),
                }))}
              />
            )}
          </Card>
        </Col>
      </If>
      <Col span={24}>
        <Card
          title={t(
            "landing.item.detail.itemDescription",
            { value0: singleNoun },
            "Item details",
          )}
        >
          <Html value={data.Description} />
        </Card>
      </Col>
      <If condition={extraLoading || !_.isEmpty(centerFaqs)}>
        {extraLoading ? (
          <Col span={24}>
            <Card>
              <Skeleton active paragraph={{ rows: 5 }} />
            </Card>
          </Col>
        ) : (
          <Faq
            pageData={layoutData}
            dataSource={{ Items: centerFaqs }}
            setting={defaultFaqSetting}
          />
        )}
      </If>
      <Col span={24}>
        {extraLoading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        ) : (
          <LandingCenterPersonnelsCard
            title={t(
              "landing.item.detail.serviceProviders",
              { value0: displayName },
              "Item details",
            )}
            model={relatedDoctors}
            itemProps={{
              BookingButton:
                !!extraData?.InternetSchedulingModule &&
                data.InternetAppointmentStatusKey ==
                CenterOptions.Enum.Status.Active.key,
              BookingServiceId: data.Id,
            }}
          />
        )}
      </Col>
      <Col span={24}>
        {extraLoading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        ) : (
          <LandingCenterGalleryCategoriesCard
            title={t(
              "landing.item.detail.itemMedia",
              { value0: singleNoun },
              "Item details",
            )}
            categories={centerGalleryCategories}
            items={centerGalleries}
          />
        )}
      </Col>
      <Col span={24}>
        {extraLoading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        ) : (
          <LandingCenterItemsCard
            title={t(
              "landing.item.detail.relatedItems",
              { value0: pluralNoun },
              "Item details",
            )}
            model={relatedItems}
          />
        )}
      </Col>
      <Col span={24}>
        {extraLoading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        ) : (
          <LandingBlogsCard
            title={t(
              "landing.item.detail.relatedBlogs",
              undefined,
              "Item details",
            )}
            model={relatedBlogs}
          />
        )}
      </Col>
      <If condition={extraLoading || shouldShowCenterDoctors}>
        <Col span={24}>
          {extraLoading ? (
            <Card>
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          ) : (
            <LandingCenterPersonnelsCard
              title={
                <>
                  {centerBranchSetting.DoctorNicknamePlural}{" "}
                  {t("common.center.centerLabel", undefined, "Item details")}
                  <CenterBranchTag model={data.Center} />
                </>
              }
              model={doctors}
            />
          )}
        </Col>
      </If>
      <Col span={24}>
        {extraLoading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        ) : (
          <LandingCenterDepartmentsCard
            title={
              <>
                {departmentPluralNoun}
                {t(
                  "landing.item.detail.centerSuffix",
                  undefined,
                  "Item details",
                )}
                <CenterBranchTag model={data.Center} />
              </>
            }
            model={departments}
          />
        )}
      </Col>
      <Col span={24}>
        <LandingCommentListPage
          TypeKey={CenterOptions.Enum.CommentType.CenterItem.key}
          CenterItemId={data.Id}
        />
      </Col>
    </Row>
  );
};

export default LandingItemDetailPageClient;
